import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { PageHeader, Loader, StatCard } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { CupsConfigForm } from "@/components/cups/CupsConfigForm";
import { api, apiError } from "@/lib/api";
import { fmtPKR, fmtDate } from "@/lib/format";

const Card = ({ title, children, testid }) => (
  <div className="ec-card p-6" data-testid={testid}>
    <h3 className="font-head text-lg font-semibold text-white mb-4">{title}</h3>{children}
  </div>
);

const Trend = ({ trend }) => {
  const max = Math.max(1, ...trend.map((d) => d.rounds));
  return (
    <div className="flex items-end gap-1 h-32" data-testid="cups-trend">
      {trend.map((d) => (
        <div key={d.date} className="flex-1 flex flex-col justify-end items-center gap-0.5" title={`${d.date}: ${d.wins}W / ${d.losses}L`}>
          <div className="w-full bg-emerald-500/70 rounded-t" style={{ height: `${(d.wins / max) * 100}px` }} />
          <div className="w-full bg-red-500/70" style={{ height: `${(d.losses / max) * 100}px` }} />
          <span className="text-[9px] text-gray-600">{d.date.slice(8)}</span>
        </div>
      ))}
    </div>
  );
};

export default function AdminCups() {
  const [data, setData] = useState(null);
  const [rounds, setRounds] = useState([]);
  const load = () => {
    api.get("/admin/cups").then(({ data }) => setData(data)).catch(() => {});
    api.get("/admin/cups/rounds").then(({ data }) => setRounds(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  if (!data) return <Loader />;
  const s = data.stats;
  const o = s.overall;

  const exportPdf = async () => {
    try {
      const res = await api.get("/admin/cups/report.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "three-cups-report.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Three Cups Game Management" subtitle="Settings, statistics and full round history."
        action={<Button onClick={exportPdf} className="bg-orange-600 hover:bg-orange-500 btn-glow" data-testid="cups-export-pdf"><FileDown className="w-4 h-4 me-2" />Export PDF</Button>} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="cups-stat-completed" label="Completed Rounds" value={o.rounds} />
        <StatCard testid="cups-stat-participants" label="Participants" value={o.participants} />
        <StatCard testid="cups-stat-winloss" label="Wins / Losses" value={`${o.wins} / ${o.losses}`} sub={`Win rate ${o.win_rate}%`} />
        <StatCard testid="cups-stat-rewards" label="Rewards Paid" value={fmtPKR(o.total_reward)} sub={`Total in ${fmtPKR(o.total_amount)}`} />
        <StatCard testid="cups-stat-win-streak" label="Longest Win Streak" value={s.longest_win_streak} />
        <StatCard testid="cups-stat-loss-streak" label="Longest Loss Streak" value={s.longest_loss_streak} />
        <StatCard testid="cups-stat-current-streak" label="Current Streak" value={s.current_streak.kind ? `${s.current_streak.length} ${s.current_streak.kind === "won" ? "wins" : "losses"}` : "-"} />
        <StatCard testid="cups-stat-open" label="Open (unpicked) Rounds" value={o.open_rounds} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <CupsConfigForm key={JSON.stringify(data.config)} cfg={data.config} onSaved={load} />
        <div className="space-y-6">
          <Card title="Review Windows (last N rounds)" testid="cups-windows">
            {s.windows.map((w) => (
              <div key={w.last} className="flex justify-between py-2 text-sm border-b border-white/5"><span className="text-white">Last {w.last}</span>
                <span className="text-gray-400">{w.rounds} rounds · {w.wins}W / {w.losses}L · {w.win_rate}% · {fmtPKR(w.total_reward)}</span></div>
            ))}
          </Card>
          <Card title="Win Milestones" testid="cups-milestones">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {s.milestones.map((m) => (
                <div key={m.wins} className="bg-white/5 rounded-xl p-3 text-center"><p className="text-xs text-gray-500">{m.wins}+ wins</p><p className="font-head text-xl font-bold text-white">{m.members}</p><p className="text-[10px] text-gray-500">members</p></div>
              ))}
            </div>
          </Card>
          <Card title="Daily Trend (14 days)" testid="cups-trend-card"><Trend trend={s.trend} /></Card>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Card title="Members" testid="cups-members">
          <div className="divide-y divide-white/5 max-h-96 overflow-y-auto">
            {s.members.length === 0 ? <p className="text-gray-500 text-sm py-6 text-center">No players yet.</p> : s.members.map((m) => (
              <div key={m.user_id} className="flex justify-between py-2 text-sm"><span className="text-white truncate">{m.name}</span>
                <span className="text-gray-400 shrink-0">{m.rounds} · {m.wins}W/{m.losses}L · streak {m.max_win_streak}W/{m.max_loss_streak}L</span></div>
            ))}
          </div>
        </Card>
        <Card title="Round History" testid="cups-admin-rounds">
          <div className="divide-y divide-white/5 max-h-96 overflow-y-auto">
            {rounds.length === 0 ? <p className="text-gray-500 text-sm py-6 text-center">No rounds yet.</p> : rounds.map((r) => (
              <div key={r.id} className="py-2 text-sm" data-testid={`cups-admin-round-${r.round_no}`}>
                <div className="flex justify-between"><span className="text-white">#{r.round_no} · {r.user_name} · {fmtPKR(r.amount)}</span>
                  <span className={r.status === "won" ? "text-emerald-400" : r.status === "lost" ? "text-red-400" : "text-yellow-400"}>
                    {r.status === "open" ? "Awaiting pick" : `${r.status === "won" ? "Win" : "Loss"} · picked ${r.picked_cup + 1} / ball ${r.winning_cup + 1}${r.status === "won" ? ` · +${fmtPKR(r.payout)}` : ""}`}</span></div>
                <p className="text-[10px] text-gray-600">{fmtDate(r.completed_at || r.created_at)}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
